import { TvScheduleDisplayMode, TvScheduleRule } from "types/TvPosterTypes";

/*
 * Regler for visningsplanen (samme som TV-appene bruker, tidssone Europe/Oslo):
 * den første regelen i listen som har minst én plakat, gjelder i dag og der
 * from <= nå < to, er aktiv.
 */

export const TIME_ZONE = "Europe/Oslo";

/** ISO-ukedager 1–7. */
export const DAY_SHORT = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const DAY_LONG = [
  "mandag",
  "tirsdag",
  "onsdag",
  "torsdag",
  "fredag",
  "lørdag",
  "søndag",
];
const MONTHS = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

/** Et øyeblikk i Oslo-tid. */
export interface Moment {
  /** «YYYY-MM-DD». */
  date: string;
  /** 1 = mandag … 7 = søndag. */
  isoDay: number;
  /** Minutter etter midnatt. */
  minutes: number;
}

/** «HH:mm» → minutter etter midnatt, eller null. */
export const toMinutes = (time: string | null | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})/.exec(time ?? "");
  if (!m) return null;
  const h = +m[1];
  const min = +m[2];
  return h > 24 || min > 59 ? null : h * 60 + min;
};

const parseDate = (date: string | null | undefined) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

/** ISO-ukedag for «YYYY-MM-DD», eller null. */
export const isoDayOf = (date: string | null | undefined): number | null => {
  const d = parseDate(date);
  return d ? ((d.getDay() + 6) % 7) + 1 : null;
};

export const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

/** Nå (eller `at`) i Oslo-tid. */
export const osloMoment = (at: Date = new Date()): Moment => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const date = `${get("year")}-${get("month")}-${get("day")}`;
  return {
    date,
    isoDay: isoDayOf(date) ?? 1,
    minutes: +get("hour") * 60 + +get("minute"),
  };
};

/** Om regelen gjelder på datoen (uavhengig av klokkeslett). */
export const appliesOn = (rule: TvScheduleRule, date: string) =>
  rule.repeat === "WEEKLY"
    ? rule.days.includes(isoDayOf(date) ?? 0)
    : rule.date === date;

/** Gyldig tidsrom: begge tider satt og `to` etter `from`. */
export const validTimes = (rule: Pick<TvScheduleRule, "from" | "to">) => {
  const a = toMinutes(rule.from);
  const b = toMinutes(rule.to);
  return a !== null && b !== null && b > a;
};

/** Den aktive regelen ved `at`, eller null (vanlig visning). */
export const activeRule = <R extends TvScheduleRule>(
  rules: R[],
  at: Moment,
): R | null =>
  rules.find(
    (r) =>
      r.posterIds.length > 0 &&
      validTimes(r) &&
      appliesOn(r, at.date) &&
      toMinutes(r.from)! <= at.minutes &&
      at.minutes < toMinutes(r.to)!,
  ) ?? null;

/** Om to regler kan gjelde på samme dag. */
const shareDay = (a: TvScheduleRule, b: TvScheduleRule) => {
  if (a.repeat === "WEEKLY" && b.repeat === "WEEKLY")
    return a.days.some((d) => b.days.includes(d));
  if (a.repeat === "DATE" && b.repeat === "DATE")
    return !!a.date && a.date === b.date;
  const [weekly, dated] = a.repeat === "WEEKLY" ? [a, b] : [b, a];
  const day = isoDayOf(dated.date);
  return day !== null && weekly.days.includes(day);
};

/**
 * Første regel over `index` som overlapper med den (og dermed gjelder i den
 * tiden), eller null. Regler uten plakater teller ikke, de blir hoppet over.
 */
export const overlappingRule = <R extends TvScheduleRule>(
  rules: R[],
  index: number,
): R | null => {
  const rule = rules[index];
  if (!rule || !validTimes(rule)) return null;
  const from = toMinutes(rule.from)!;
  const to = toMinutes(rule.to)!;
  return (
    rules
      .slice(0, index)
      .find(
        (o) =>
          o.posterIds.length > 0 &&
          validTimes(o) &&
          shareDay(rule, o) &&
          toMinutes(o.from)! < to &&
          from < toMinutes(o.to)!,
      ) ?? null
  );
};

export interface RuleErrors {
  name?: string;
  posters?: string;
  when?: string;
  time?: string;
}

export const validateRule = (rule: TvScheduleRule): RuleErrors => {
  const errors: RuleErrors = {};
  if (!rule.name.trim()) errors.name = "Gi visningen et navn.";
  if (rule.posterIds.length === 0) errors.posters = "Velg minst én plakat.";
  if (rule.repeat === "WEEKLY" && rule.days.length === 0)
    errors.when = "Velg minst én dag.";
  if (rule.repeat === "DATE" && !parseDate(rule.date))
    errors.when = "Velg en dato.";
  if (toMinutes(rule.from) === null || toMinutes(rule.to) === null)
    errors.time = "Fyll inn fra og til.";
  else if (!validTimes(rule))
    errors.time = "Til-tiden må være etter fra-tiden.";
  return errors;
};

export const hasErrors = (errors: RuleErrors) =>
  Object.values(errors).some(Boolean);

const listText = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} og ${items[items.length - 1]}`;

const capitalize = (text: string) =>
  text ? text[0].toUpperCase() + text.slice(1) : text;

/** «Lørdag 17. oktober». */
export const formatDate = (date: string | null | undefined) => {
  const d = parseDate(date);
  if (!d) return null;
  return `${capitalize(DAY_LONG[(d.getDay() + 6) % 7])} ${d.getDate()}. ${
    MONTHS[d.getMonth()]
  }`;
};

/** «Hver fredag», «Hver dag», «Lørdag 17. oktober». */
export const whenText = (rule: TvScheduleRule) => {
  if (rule.repeat === "DATE")
    return formatDate(rule.date) ?? "Ingen dato valgt";
  const days = Array.from(new Set(rule.days)).sort((a, b) => a - b);
  if (days.length === 0) return "Ingen dager valgt";
  if (days.length === 7) return "Hver dag";
  return `Hver ${listText(days.map((d) => DAY_LONG[d - 1]))}`;
};

export const DEFAULT_DISPLAY_MODE: TvScheduleDisplayMode =
  "POSTER_WITH_PRAYERS";

/** Valgene under «Slik vises den», i visningsrekkefølge. */
export const DISPLAY_MODES: Array<{
  value: TvScheduleDisplayMode;
  label: string;
  description: string;
}> = [
  {
    value: "POSTER_WITH_PRAYERS",
    label: "Plakat med bønnetider",
    description: "Plakaten stort, med bønnetidene og nedtelling nederst.",
  },
  {
    value: "POSTER_ONLY",
    label: "Bare plakaten",
    description: "Plakaten fyller hele skjermen, uten klokke og bønnetider.",
  },
  {
    value: "CAROUSEL",
    label: "Vanlig visning",
    description: "TV-en ser ut som vanlig, men karusellen viser bare disse.",
  },
];

/** Visningsmåten til regelen (eldre regler uten verdi → standard). */
export const displayModeOf = (rule: Pick<TvScheduleRule, "displayMode">) =>
  DISPLAY_MODES.some((m) => m.value === rule.displayMode)
    ? rule.displayMode!
    : DEFAULT_DISPLAY_MODE;

export const displayModeLabel = (mode: TvScheduleDisplayMode) =>
  DISPLAY_MODES.find((m) => m.value === mode)?.label ?? mode;

/** Én setning som forklarer regelen. */
export const summary = (
  rule: TvScheduleRule,
  posterName: (id: number) => string | undefined,
) => {
  const when = `${whenText(rule)} kl. ${rule.from || "?"}–${rule.to || "?"}`;
  const names = rule.posterIds
    .map(posterName)
    .filter((name): name is string => !!name)
    .map((name) => `«${name}»`);
  if (names.length === 0) return `${when}. Velg minst én plakat.`;
  const list = listText(names);
  switch (displayModeOf(rule)) {
    case "POSTER_ONLY":
      return `${when} vises bare ${list} i hele skjermen.`;
    case "CAROUSEL":
      return `${when} står TV-en i vanlig visning, men karusellen viser bare ${list}.`;
    default:
      return `${when} vises bare ${list}, med bønnetidene nederst.`;
  }
};

/** Mandag–søndag i uken som inneholder `date` («YYYY-MM-DD»). */
export const weekDates = (date: string): string[] => {
  const d = parseDate(date) ?? new Date();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    return toIsoDate(day);
  });
};

/** Ny regel med fornuftige standardverdier. */
export const newRule = (): TvScheduleRule => ({
  id: null,
  name: "Ny tidsstyrt visning",
  posterIds: [],
  repeat: "WEEKLY",
  days: [5],
  date: null,
  from: "12:00",
  to: "14:00",
  displayMode: DEFAULT_DISPLAY_MODE,
});

/** Flytter elementet på `index` ett steg (`delta` = -1 opp, 1 ned). */
export const move = <T>(list: T[], index: number, delta: number): T[] => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};
